import { apiClient } from "@/lib/apiClient";
import { getAppointmentRegistry } from "@/lib/appointment/appointmentRegistrySingleton";
import { DateTime } from 'luxon';
import type { Appointment } from "@/lib/types";

/**
 * Param generators for each request.
 */
function getRequest1Params(date: string): string {
  const dateStr = DateTime.fromISO(date, { zone: 'UTC' }).toISODate();
  return (
    `dates%5B%5D=${encodeURIComponent(dateStr)}` +
    "&include%5B%5D=records" +
    "&include%5B%5D=records.services" +
    "&include%5B%5D=records.labels" +
    "&include%5B%5D=records.master" +
    "&include%5B%5D=records.client" +
    "&include%5B%5D=records.resource_instances.resource" +
    "&include%5B%5D=records.resource_instances" +
    "&include%5B%5D=records.acceptance_free" +
    "&include%5B%5D=records.duration_details" +
    "&include%5B%5D=records.attendance_service_items.attendance_service_composite.composite_service" +
    "&include%5B%5D=activities" +
    "&include%5B%5D=activities.service" +
    "&include%5B%5D=activities.labels" +
    "&include%5B%5D=activities.master" +
    "&include%5B%5D=activities.resource_instances.resource" +
    "&include%5B%5D=activities.resource_instances" +
    "&new_timetable_attendance_from_legacy=1"
  );
}

function getRequest6Params(weekStart: string): string {
  // weekStart is expected to be a YYYY-MM-DD string (Monday)
  const start = DateTime.fromISO(weekStart, { zone: 'UTC' });
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = start.plus({ days: i });
    dates.push(d.toISODate());
  }
  const dateParams = dates.map(date => `dates[]=${encodeURIComponent(date)}`).join("&");
  return (
    `${dateParams}` +
    "&include[]=records" +
    "&include[]=records.services" +
    "&include[]=records.labels" +
    "&include[]=records.master" +
    "&include[]=records.client" +
    "&include[]=records.resource_instances.resource" +
    "&include[]=records.resource_instances" +
    "&include[]=records.acceptance_free" +
    "&include[]=records.duration_details" +
    "&include[]=records.attendance_service_items.attendance_service_composite.composite_service" +
    "&include[]=activities" +
    "&include[]=activities.service" +
    "&include[]=activities.labels" +
    "&include[]=activities.master" +
    "&include[]=activities.resource_instances.resource" +
    "&include[]=activities.resource_instances" +
    "&attendance_for_week=1"
  );
}

function getRequest10Params(staffId: number, weekDates: string[]): string {
  const dateParams = weekDates.map(date => `dates[]=${encodeURIComponent(date)}`).join("&");
  const masterIds = `master_ids[]=${encodeURIComponent(staffId)}`;
  return (
    `${dateParams}&${masterIds}` +
    "&include[]=records" +
    "&include[]=records.services" +
    "&include[]=records.labels" +
    "&include[]=records.master" +
    "&include[]=records.client" +
    "&include[]=records.resource_instances.resource" +
    "&include[]=records.resource_instances" +
    "&include[]=records.acceptance_free" +
    "&include[]=records.duration_details" +
    "&include[]=records.attendance_service_items.attendance_service_composite.composite_service" +
    "&include[]=activities" +
    "&include[]=activities.service" +
    "&include[]=activities.labels" +
    "&include[]=activities.master" +
    "&include[]=activities.resource_instances.resource" +
    "&include[]=activities.resource_instances" +
    "&attendance_for_week=1"
  );
}

/**
 * Fetch daily appointments for a given date and optional staffId.
 * Returns UI-ready, normalized data.
 */
export async function fetchDailyAppointments(date: string, staffId?: number): Promise<Appointment[]> {
  const params = getRequest1Params(date);
  const { data } = await apiClient.get(`/api/v1/company/timetable/attendance_day?${params}`);
  const registry = getAppointmentRegistry();
  await registry.parseResponse(data);
  return registry.formatAppointmentsForDate(date, staffId);
}

/**
 * Fetch week appointments (Request #6).
 * Returns UI-ready, normalized data.
 */
export async function fetchWeekAppointments(weekStart: string): Promise<Record<string, Appointment[]>> {
  const params = getRequest6Params(weekStart);
  const { data } = await apiClient.get(`/api/v1/company/timetable/attendance_day?${params}`);
  const registry = getAppointmentRegistry();
  await registry.parseResponse(data);
  const start = DateTime.fromISO(weekStart, { zone: 'UTC' });
  const weekDates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const d = start.plus({ days: i });
    weekDates.push(d.toISODate());
  }
  return registry.formatAppointmentsForWeek(weekDates);
}

/**
 * Fetch week appointments/attendance for a staff member (Request #10).
 * Returns UI-ready, normalized data.
 */
export async function fetchWeekAppointmentsForStaff(staffId: number, weekDates: string[]): Promise<Record<string, Appointment[]>> {
  const params = getRequest10Params(staffId, weekDates);
  const { data } = await apiClient.get(`/api/v1/company/timetable/attendance_day?${params}`);
  const registry = getAppointmentRegistry();
  await registry.parseResponse(data);
  return registry.formatAppointmentsForWeekForStaff(staffId, weekDates);
}
